import { useState } from 'react';
import { useNavigateTransition } from '../lib/navigation';
import { useFlow } from '../state/flow';
import { checkNames } from '../lib/namecheck';
import { trackEvent } from '../lib/analytics';
import { ScaledReportCard } from '../components/ReportCard';
import IdCardTeaser from '../components/IdCardTeaser';
import { EPISODES } from '../data/episodes';
import { QUIZ_COPY } from '../lib/quizCopy';
import './QuizStart.scss';

/** Quiz picker — serves as both the intro screen and the "pick another quiz"
 * screen (DRY). Completed episodes render a skeleton report-card preview;
 * the rest show a Start Quiz button. Episode buttons are disabled until a
 * first name is entered. */
export default function QuizStart() {
  const navigate = useNavigateTransition();
  const { form, setName, quizResults } = useFlow();
  const [first, setFirst] = useState(form.firstName);
  const [last, setLast] = useState(form.lastName);
  const [checking, setChecking] = useState(false);
  const [nameError, setNameError] = useState('');

  const hasAnyResult = Object.keys(quizResults).length > 0;
  const canStart = first.trim().length > 0 && !checking;

  function commitName() {
    setName(first.trim(), last.trim());
  }

  async function start(episode: number) {
    if (first.trim().length === 0 || checking) return;
    setChecking(true);
    const res = await checkNames(first.trim(), last.trim());
    setChecking(false);
    if (!res.ok) {
      setNameError(QUIZ_COPY.nameError);
      trackEvent('namecheck_blocked', { field: res.field ?? 'firstName' });
      return;
    }
    setName(first.trim(), last.trim());
    trackEvent('quiz_started', { episode });
    navigate(`/class/${episode}`);
  }

  return (
    <div className="quiz-start">
      <div className="quiz-start__inner">
        <h1 className="quiz-start__title">
          {hasAnyResult ? (
            QUIZ_COPY.againTitle
          ) : (
            <>
              Ready to test your
              {/* Desktop breaks before "Off Campus knowledge"; mobile wraps naturally. */}
              <br className="quiz-start__title-br" /> Off Campus knowledge?
            </>
          )}
        </h1>

        <div className="quiz-start__name">
          <label className="quiz-start__field">
            <span className="visually-hidden">{QUIZ_COPY.firstName}</span>
            <input
              type="text"
              placeholder={QUIZ_COPY.firstName}
              value={first}
              maxLength={30}
              autoComplete="given-name"
              aria-invalid={!!nameError}
              onChange={(e) => {
                setFirst(e.target.value);
                if (nameError) setNameError('');
              }}
              onBlur={commitName}
            />
          </label>
          <label className="quiz-start__field">
            <span className="visually-hidden">{QUIZ_COPY.lastName}</span>
            <input
              type="text"
              placeholder={QUIZ_COPY.lastName}
              value={last}
              maxLength={30}
              autoComplete="family-name"
              onChange={(e) => setLast(e.target.value)}
              onBlur={commitName}
            />
          </label>
          {nameError && <p className="quiz-start__error">{nameError}</p>}
        </div>

        <p className="quiz-start__prompt">{QUIZ_COPY.pickPrompt}</p>

        <ul className={`quiz-start__grid${hasAnyResult ? ' quiz-start__grid--gallery' : ''}`}>
          {EPISODES.map((ep) => {
            const result = quizResults[ep.n];
            return (
              <li key={ep.n} className="quiz-start__cell">
                <p className="quiz-start__cell-label">Episode {ep.n}</p>
                <button
                  type="button"
                  className={`quiz-start__cell-btn${result ? ' quiz-start__cell-btn--done' : ''}`}
                  onClick={() => start(ep.n)}
                  disabled={!canStart}
                  aria-label={
                    result ? `Retake the Episode ${ep.n} quiz` : `Start the Episode ${ep.n} quiz`
                  }
                >
                  {result ? (
                    <ScaledReportCard result={result} variant="preview" />
                  ) : (
                    <span className="quiz-start__start">{QUIZ_COPY.startQuiz}</span>
                  )}
                </button>
              </li>
            );
          })}

          {/* 8th slot — fills the grid evenly and cross-links to the ID-card
              flow: a CTA in the fresh view, a skeleton ID-card teaser once the
              grid expands to thumbnails. */}
          <li className="quiz-start__cell">
            <p className="quiz-start__cell-label">
              {hasAnyResult ? QUIZ_COPY.getYourIdCard : QUIZ_COPY.studentId}
            </p>
            <button
              type="button"
              className="quiz-start__cell-btn"
              onClick={() => navigate('/id-card')}
              aria-label="Get your Briar University ID card"
            >
              {hasAnyResult ? (
                <IdCardTeaser />
              ) : (
                <span className="quiz-start__start">{QUIZ_COPY.getYourIdCard}</span>
              )}
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
}
